/**
 * useAnimatedValue Hook
 *
 * Smoothly interpolates between old and new numeric values using
 * requestAnimationFrame with ease-out cubic easing.
 *
 * Created: March 2026 (Phase 5: Data Hooks & Animation)
 */

import { useState, useRef, useEffect } from 'react';

/**
 * Smoothly interpolates between old and new numeric values using
 * requestAnimationFrame with ease-out cubic easing.
 *
 * @param targetValue - The value to animate toward
 * @param duration - Animation duration in ms (default 300)
 * @param enabled - Set false to skip animation (returns targetValue immediately)
 * @returns The current interpolated value
 */
export function useAnimatedValue(
  targetValue: number,
  duration: number = 300,
  enabled: boolean = true,
): number {
  const [displayValue, setDisplayValue] = useState(targetValue);
  const prevValueRef = useRef(targetValue);
  const rafRef = useRef<number>();

  useEffect(() => {
    if (!enabled || prevValueRef.current === targetValue || duration <= 0) {
      setDisplayValue(targetValue);
      prevValueRef.current = targetValue;
      return;
    }

    // Note: when targetValue changes mid-animation, the new animation starts from
    // the previous target (prevValueRef), not the current visual position. This is
    // intentional — capturing displayValue would create a dependency cycle.
    const startValue = prevValueRef.current;
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease-out cubic: 1 - (1 - t)^3
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = startValue + (targetValue - startValue) * eased;
      setDisplayValue(current);

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        prevValueRef.current = targetValue;
      }
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      // AUDIT FIX: update ref on cleanup (unmount mid-animation)
      // Prevents visual glitch on rapid mount/unmount
      prevValueRef.current = targetValue;
    };
  }, [targetValue, duration, enabled]);

  return displayValue;
}
